import React, { useEffect, useMemo, useState } from "react";
import { BarsOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Modal,
  Row,
  Select,
  Spin,
  Table,
} from "antd";
import { useHistory, useLocation } from "react-router-dom";
import moment from "moment";
import RequestHandler from "../../util/RequestHandler";
import { ColumnHelper } from "../../util/TableHelper";

const CREDIT_TRANSACTION_PAGE_SIZE = 10;
const DEFAULT_FILTER_DATE = "1970-01-01T00:00:00";

const EMPTY_STATE_STYLE = {
  minHeight: 220,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "32px 16px",
};

const getField = (source, fieldNames, fallback = undefined) => {
  for (const fieldName of fieldNames) {
    if (source?.[fieldName] !== undefined && source?.[fieldName] !== null) {
      return source[fieldName];
    }
  }

  return fallback;
};

const getFirstValue = (sources, fieldNames, fallback = "") => {
  for (const source of sources) {
    const value = getField(source, fieldNames);
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }

  return fallback;
};

const normalizeTransactionResponse = (response) => {
  const rows = Array.isArray(response)
    ? response
    : Array.isArray(response?.value)
      ? response.value
      : Array.isArray(response?.data)
        ? response.data
        : [];

  const total =
    response?.count ??
    response?.Count ??
    response?.totalCount ??
    response?.TotalCount ??
    rows.length;

  return {
    data: rows,
    total,
  };
};

const formatAmountWithCurrency = (amount, currency) => {
  if (amount === "" || amount === null || amount === undefined) {
    return "";
  }

  const formattedAmount = ColumnHelper.renderDecimal(amount);
  return currency ? `${formattedAmount} ${currency}` : formattedAmount;
};

const formatDetailDate = (value) => {
  if (!value) return "";

  const parsedDate = moment(value);
  if (!parsedDate.isValid()) return "";

  const year = parsedDate.year();
  const month = parsedDate.month();
  const day = parsedDate.date();

  if ((year === 1 && month === 0 && day === 1) || (year === 1970 && month === 0 && day === 1)) {
    return "";
  }

  return parsedDate.format("DD.MM.YYYY");
};

const buildStatementDateOptions = (periodEndDateValue) => {
  const parsedDate = moment(periodEndDateValue);
  if (!parsedDate.isValid()) {
    return [];
  }

  return Array.from({ length: 12 }, (_, index) => {
    const periodOffset = index + 1;
    const periodDate = parsedDate.clone().subtract(periodOffset, "month");
    const periodValue = -periodOffset;

    return {
      month: periodDate.month() + 1,
      year: periodDate.year(),
      periodValue,
      key: `${periodDate.year()}-${periodDate.month() + 1}-${periodValue}`,
      displayDate: periodDate.format("MM/YYYY"),
    };
  });
};

const buildTransactionPayload = (tenantCardId, statementCurrency, periodOption, page) => ({
  tenantCardId,
  statementCurrency,
  periodEndMonth: periodOption.month,
  periodEndYear: periodOption.year,
  currentPeriodOnly: false,
  startDate: DEFAULT_FILTER_DATE,
  endDate: DEFAULT_FILTER_DATE,
  debitCreditIndicator: "",
  firstData: 0,
  count: CREDIT_TRANSACTION_PAGE_SIZE,
  page,
  order: "",
});

const BkmCardStatementDetail = () => {
  const history = useHistory();
  const location = useLocation();
  const selectedCard = location.state?.selectedCard;
  const statementCurrency = location.state?.statementCurrency;

  const [periodOptions, setPeriodOptions] = useState([]);
  const [selectedPeriodKey, setSelectedPeriodKey] = useState(undefined);
  const [summaryData, setSummaryData] = useState(null);
  const [transactions, setTransactions] = useState({
    loading: false,
    data: [],
    total: 0,
  });
  const [page, setPage] = useState(1);
  const [loadingCurrentPeriod, setLoadingCurrentPeriod] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [detailModalRecord, setDetailModalRecord] = useState(null);

  const selectedPeriod = useMemo(
    () => periodOptions.find((item) => item.key === selectedPeriodKey),
    [periodOptions, selectedPeriodKey],
  );

  useEffect(() => {
    if (!selectedCard?.id || !statementCurrency) {
      history.replace({
        pathname: "/other-bank-cards",
        state: {
          activeTab: "credit",
          useSecondaryTokenOnLoad: true,
        },
      });
      return;
    }

    loadInitialData();
  }, [history, selectedCard?.id, statementCurrency]);

  const loadInitialData = async () => {
    setLoadingCurrentPeriod(true);

    const currentPeriodResponse = await RequestHandler.get(
      `/CardTransactionPeriod/GetCurrentPeriod?tenantCardId=${selectedCard.id}&statementCurrency=${statementCurrency}`,
    );

    setLoadingCurrentPeriod(false);

    if (currentPeriodResponse === undefined) return;

    const periodEndDate = getFirstValue(
      [currentPeriodResponse, currentPeriodResponse?.data],
      ["periodEndDate", "PeriodEndDate"],
      "",
    );

    const options = buildStatementDateOptions(periodEndDate);
    setPeriodOptions(options);

    if (options.length === 0) {
      setSelectedPeriodKey(undefined);
      setSummaryData(null);
      setTransactions({
        loading: false,
        data: [],
        total: 0,
      });
      return;
    }

    setSelectedPeriodKey(options[0].key);
    await loadPeriodData(options[0]);
  };

  const loadPeriodData = async (periodOption) => {
    if (!selectedCard?.id || !statementCurrency || !periodOption) return;

    setLoadingSummary(true);
    setTransactions((prevState) => ({
      ...prevState,
      loading: true,
    }));
    setPage(1);

    const syncResponse = await RequestHandler.get(
      `/BkmCardSync/SyncCardTransactions/${selectedCard.id}?periodValue=${periodOption.periodValue}&statementCurrency=${statementCurrency}`,
    );

    if (syncResponse === undefined) {
      setLoadingSummary(false);
      setTransactions((prevState) => ({
        ...prevState,
        loading: false,
      }));
      return;
    }

    const summaryResponse = await RequestHandler.get(
      `/CardTransactionPeriod/GetPeriodByMonthYear?tenantCardId=${selectedCard.id}&periodEndMonth=${periodOption.month}&periodEndYear=${periodOption.year}&statementCurrency=${statementCurrency}`,
    );

    if (summaryResponse === undefined) {
      setLoadingSummary(false);
      setTransactions((prevState) => ({
        ...prevState,
        loading: false,
      }));
      return;
    }

    setSummaryData(summaryResponse?.data || summaryResponse?.period || summaryResponse || {});
    setLoadingSummary(false);

    await loadTransactions(periodOption, 1);
  };

  const loadTransactions = async (periodOption, nextPage) => {
    if (!selectedCard?.id || !statementCurrency || !periodOption) return;

    setTransactions((prevState) => ({
      ...prevState,
      loading: true,
    }));

    const response = await RequestHandler.post(
      `/CardTransaction/GetListByFilter`,
      buildTransactionPayload(
        selectedCard.id,
        statementCurrency,
        periodOption,
        nextPage,
      ),
      false,
    );

    const normalized = normalizeTransactionResponse(response);

    setTransactions({
      loading: false,
      data: normalized.data,
      total: normalized.total,
    });
    setPage(nextPage);
  };

  const handlePeriodChange = async (periodKey) => {
    const nextPeriod = periodOptions.find((item) => item.key === periodKey);
    if (!nextPeriod) return;

    setSelectedPeriodKey(periodKey);
    await loadPeriodData(nextPeriod);
  };

  const handleBack = () => {
    history.push({
      pathname: "/other-bank-cards",
      state: {
        activeTab: "credit",
        restoreSelectedCardId: selectedCard.id,
        restoreStatementCurrency: statementCurrency,
        useSecondaryTokenOnLoad: true,
      },
    });
  };

  const getSummaryAmount = (amountFieldNames, currencyFieldNames) =>
    formatAmountWithCurrency(
      getFirstValue([summaryData], amountFieldNames, ""),
      getFirstValue([summaryData], currencyFieldNames, ""),
    );

  const hasSummaryData = useMemo(() => {
    if (!summaryData) return false;

    const amountFieldGroups = [
      ["statementDebtAmount", "StatementDebtAmount"],
      ["minimumPaymentAmount", "MinimumPaymentAmount"],
    ];

    const hasAmountValue = amountFieldGroups.some((fieldNames) => {
      const value = getField(summaryData, fieldNames);
      return value !== undefined && value !== null && value !== "";
    });

    const hasDateValue =
      formatDetailDate(
        getFirstValue([summaryData], ["statementDate", "StatementDate"], ""),
      ) !== "" ||
      formatDetailDate(
        getFirstValue([summaryData], ["paymentDueDate", "PaymentDueDate"], ""),
      ) !== "";

    return hasAmountValue || hasDateValue;
  }, [summaryData]);

  const transactionColumns = [
    {
      title: "İşlem Tarihi",
      dataIndex: "transactionDate",
      key: "transactionDate",
      render: (_, row) =>
        formatDetailDate(
          getFirstValue([row], ["transactionDate", "TransactionDate"], ""),
        ),
    },
    {
      title: "Açıklama",
      dataIndex: "description",
      key: "description",
      render: (_, row) =>
        getFirstValue(
          [row],
          [
            "transactionDescription",
            "TransactionDescription",
            "description",
            "Description",
          ],
          "",
        ),
    },
    {
      title: "İşlem Tutarı",
      dataIndex: "transactionAmount",
      key: "transactionAmount",
      render: (_, row) => {
        const amount = getFirstValue(
          [row],
          ["transactionAmount", "TransactionAmount", "amount", "Amount"],
          "",
        );
        const currency = getFirstValue(
          [row],
          [
            "transactionAmountCurrency",
            "TransactionAmountCurrency",
            "currencyCode",
            "CurrencyCode",
          ],
          "",
        );
        const isDebit =
          getFirstValue(
            [row],
            ["debitCreditIndicator", "DebitCreditIndicator"],
            "",
          ) === "B" ||
          getFirstValue(
            [row],
            ["transactionProcessType", "TransactionProcessType"],
            "",
          ) === 2;

        return (
          <span style={isDebit ? { color: "#ff4d4f", fontWeight: 600 } : {}}>
            {formatAmountWithCurrency(amount, currency)}
          </span>
        );
      },
    },
  ];

  if (!selectedCard?.id || !statementCurrency) return null;

  return (
    <>
      <Row justify="space-between" align="middle" gutter={[12, 12]}>
        <Col>
          <p style={{ fontSize: "120%", marginTop: -5, marginBottom: 12 }}>
            <BarsOutlined /> KREDİ KARTINIZA AİT EKSTRE DETAY BİLGİLERİNİZ :
          </p>
        </Col>
        <Col>
          <Button onClick={handleBack}>Geri</Button>
        </Col>
      </Row>

      <Card size="small" style={{ borderRadius: 6, marginBottom: 12 }}>
        {loadingCurrentPeriod ? (
          <Row justify="center" style={{ padding: "24px 0" }}>
            <Spin />
          </Row>
        ) : (
          <Row gutter={[12, 12]}>
            <Col xs={24} md={8}>
              <label style={{ display: "block", marginBottom: 6 }}>
                Ekstre Tarihi
              </label>
              <Select
                value={selectedPeriodKey}
                onChange={handlePeriodChange}
                style={{ width: "100%" }}
                placeholder="Dönem seçiniz"
              >
                {periodOptions.map((periodOption) => (
                  <Select.Option
                    key={periodOption.key}
                    value={periodOption.key}
                  >
                    {periodOption.displayDate}
                  </Select.Option>
                ))}
              </Select>
            </Col>
          </Row>
        )}
      </Card>

      <Card
        size="small"
        title="Ekstre ve Borç Bilgileriniz"
        style={{ borderRadius: 6, marginBottom: 12 }}
        bodyStyle={{ padding: 0 }}
      >
        {loadingSummary ? (
          <Row justify="center" style={{ padding: "24px 0" }}>
            <Spin />
          </Row>
        ) : hasSummaryData ? (
          <Table
            size="small"
            pagination={false}
            dataSource={[
              {
                key: "summary",
                statementDebt: getSummaryAmount(
                  ["statementDebtAmount", "StatementDebtAmount"],
                  ["statementDebtCurrency", "StatementDebtCurrency"],
                ),
                minimumPayment: getSummaryAmount(
                  ["minimumPaymentAmount", "MinimumPaymentAmount"],
                  ["minimumPaymentCurrency", "MinimumPaymentCurrency"],
                ),
                statementDate: formatDetailDate(
                  getFirstValue(
                    [summaryData],
                    ["statementDate", "StatementDate"],
                    "",
                  ),
                ),
                paymentDueDate: formatDetailDate(
                  getFirstValue(
                    [summaryData],
                    ["paymentDueDate", "PaymentDueDate"],
                    "",
                  ),
                ),
              },
            ]}
            columns={[
              {
                title: "Ekstre Borcu",
                dataIndex: "statementDebt",
                key: "statementDebt",
              },
              {
                title: "Asgari Ödeme",
                dataIndex: "minimumPayment",
                key: "minimumPayment",
              },
              {
                title: "Hesap Kesim Tarihi",
                dataIndex: "statementDate",
                key: "statementDate",
              },
              {
                title: "Son Ödeme Tarihi",
                dataIndex: "paymentDueDate",
                key: "paymentDueDate",
              },
            ]}
          />
        ) : (
          <div style={EMPTY_STATE_STYLE}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Veri Yok"
            />
          </div>
        )}
      </Card>

      <Card
        size="small"
        title="Dönem Ekstre Hareketleriniz"
        style={{ borderRadius: 6 }}
        bodyStyle={{ padding: 0 }}
      >
        {transactions.loading || transactions.data.length > 0 ? (
          <Table
            size="small"
            columns={transactionColumns}
            dataSource={transactions.data}
            loading={transactions.loading}
            rowKey={(row, index) => row?.id || row?.Id || index}
            pagination={{
              current: page,
              pageSize: CREDIT_TRANSACTION_PAGE_SIZE,
              total: transactions.total,
              showSizeChanger: false,
              onChange: (nextPage) => loadTransactions(selectedPeriod, nextPage),
            }}
            onRow={(record) => ({
              onClick: () => setDetailModalRecord(record),
              style: { cursor: "pointer" },
            })}
          />
        ) : (
          <div style={EMPTY_STATE_STYLE}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Veri Yok"
            />
          </div>
        )}
      </Card>

      <Modal
        visible={!!detailModalRecord}
        footer={null}
        title="İşlem Detayı"
        onCancel={() => setDetailModalRecord(null)}
      >
        {detailModalRecord && (
          <Descriptions size="small" column={1} bordered>
            <Descriptions.Item label="İşlem Tarihi">
              {formatDetailDate(
                getFirstValue(
                  [detailModalRecord],
                  ["transactionDate", "TransactionDate"],
                  "",
                ),
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Kanal">Mobil</Descriptions.Item>
            <Descriptions.Item label="İşlem Tutarı">
              {formatAmountWithCurrency(
                getFirstValue(
                  [detailModalRecord],
                  ["transactionAmount", "TransactionAmount", "amount", "Amount"],
                  "",
                ),
                getFirstValue(
                  [detailModalRecord],
                  [
                    "transactionAmountCurrency",
                    "TransactionAmountCurrency",
                    "currencyCode",
                    "CurrencyCode",
                  ],
                  "",
                ),
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Açıklama">
              {getFirstValue(
                [detailModalRecord],
                [
                  "transactionDescription",
                  "TransactionDescription",
                  "description",
                  "Description",
                ],
                "",
              )}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </>
  );
};

export default BkmCardStatementDetail;
